import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Bot, Send, Plus, CheckCheck, Sparkles, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface FAQ {
  id?: string;
  question: string;
  answer: string;
  displayOrder: number;
}

interface Message {
  role: "user" | "assistant";
  content: string;
  suggestedFaqs?: Array<{ question: string; answer: string }>;
}

interface SopBuilderChatProps {
  sopTitle: string;
  existingFaqs: FAQ[];
  onAddFaqs: (faqs: Array<{ question: string; answer: string }>) => void;
  onClose: () => void;
}

const SUGGESTED_FAQS_RE = /SUGGESTED_FAQS:(\[[\s\S]*?\])\s*$/;

function parseSuggestedFaqs(raw: string): {
  displayText: string;
  faqs: Array<{ question: string; answer: string }>;
} {
  const match = raw.match(SUGGESTED_FAQS_RE);
  if (!match) return { displayText: raw, faqs: [] };
  let faqs: Array<{ question: string; answer: string }> = [];
  try {
    faqs = JSON.parse(match[1]);
  } catch {
    faqs = [];
  }
  const displayText = raw.slice(0, match.index).trimEnd();
  return { displayText, faqs };
}

const OPENING_MESSAGE: Message = {
  role: "assistant",
  content:
    "Hi! I'm your SOP builder. I'll interview you to capture your working preferences, scheduling rules, and VA guidelines — so any AI or virtual assistant can hit the ground running without you having to retrain them each time.\n\nLet me pull up what I already know about you, and then we'll fill in the gaps together. To get started: what's the main purpose of this SOP? Is it about how you want to be reached, how your schedule works, how you want tasks handled — or something else?",
};

export function SopBuilderChat({ sopTitle, existingFaqs, onAddFaqs, onClose }: SopBuilderChatProps) {
  const [messages, setMessages] = useState<Message[]>([OPENING_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [addedIndexes, setAddedIndexes] = useState<Set<string>>(new Set());
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Send opening context message automatically on first render
  useEffect(() => {
    sendBuilderMessage("__INIT__");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function sendBuilderMessage(userText: string) {
    const isInit = userText === "__INIT__";
    const history = isInit
      ? []
      : messages
          .filter((m) => !(m.role === "assistant" && m === OPENING_MESSAGE))
          .map((m) => ({ role: m.role, content: m.content }));

    const body: Record<string, unknown> = {
      message: isInit
        ? `I'm opening the SOP builder for the SOP titled "${sopTitle || "Untitled SOP"}". Please greet me briefly, then look up what you already know about my goals, guardrails, habits, and calendar using your tools. After checking, ask me your first discovery question about my working preferences.`
        : userText,
      conversationHistory: history,
      sopTitle,
      sopContext:
        existingFaqs.length > 0
          ? existingFaqs.map((f) => `Q: ${f.question}\nA: ${f.answer}`).join("\n\n")
          : undefined,
    };

    setLoading(true);
    try {
      const res = await fetch("/api/ai-coach/sop-builder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      const raw: string = data.response ?? "";
      const { displayText, faqs } = parseSuggestedFaqs(raw);
      if (isInit) {
        // Replace placeholder opening message with real AI response
        setMessages([{ role: "assistant", content: displayText, suggestedFaqs: faqs }]);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: displayText, suggestedFaqs: faqs },
        ]);
      }
    } catch {
      toast({ title: "Couldn't reach the AI. Try again in a moment.", variant: "destructive" });
      if (isInit) setMessages([OPENING_MESSAGE]);
    } finally {
      setLoading(false);
    }
  }

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    await sendBuilderMessage(text);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const addFaq = (msgIdx: number, faqIdx: number, faq: { question: string; answer: string }) => {
    const key = `${msgIdx}-${faqIdx}`;
    if (addedIndexes.has(key)) return;
    onAddFaqs([faq]);
    setAddedIndexes((prev) => new Set([...prev, key]));
    toast({ title: "FAQ added to this SOP" });
  };

  const addAllFaqs = (msgIdx: number, faqs: Array<{ question: string; answer: string }>) => {
    const toAdd = faqs.filter((_, i) => !addedIndexes.has(`${msgIdx}-${i}`));
    if (toAdd.length === 0) return;
    onAddFaqs(toAdd);
    setAddedIndexes((prev) => {
      const next = new Set(prev);
      faqs.forEach((_, i) => next.add(`${msgIdx}-${i}`));
      return next;
    });
    toast({ title: `${toAdd.length} FAQ${toAdd.length > 1 ? "s" : ""} added to this SOP` });
  };

  return (
    <div className="flex flex-col h-full border rounded-lg bg-background overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold leading-none">SOP Builder AI</p>
            <p className="text-xs text-muted-foreground mt-0.5">Captures your preferences as FAQs</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-3">
        <div className="space-y-4">
          {messages.map((msg, msgIdx) => (
            <div key={msgIdx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] space-y-2`}>
                {msg.role === "assistant" && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <Bot className="w-3.5 h-3.5 text-primary" />
                    <span className="text-xs font-medium text-primary">SOP Builder</span>
                  </div>
                )}
                <div
                  className={`rounded-xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted/60 text-foreground"
                  }`}
                >
                  {msg.content}
                </div>

                {/* Suggested FAQs */}
                {msg.role === "assistant" && msg.suggestedFaqs && msg.suggestedFaqs.length > 0 && (
                  <div className="space-y-2 mt-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-xs gap-1 text-primary border-primary/30">
                        <Sparkles className="w-3 h-3" />
                        Suggested {msg.suggestedFaqs.length === 1 ? "FAQ" : `${msg.suggestedFaqs.length} FAQs`}
                      </Badge>
                      {msg.suggestedFaqs.length > 1 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-xs px-2"
                          onClick={() => addAllFaqs(msgIdx, msg.suggestedFaqs!)}
                        >
                          <CheckCheck className="w-3 h-3 mr-1" />
                          Add all
                        </Button>
                      )}
                    </div>
                    {msg.suggestedFaqs.map((faq, faqIdx) => {
                      const key = `${msgIdx}-${faqIdx}`;
                      const added = addedIndexes.has(key);
                      return (
                        <div key={faqIdx} className="rounded-lg border bg-card p-3 space-y-1">
                          <p className="text-xs font-semibold text-foreground">{faq.question}</p>
                          <p className="text-xs text-muted-foreground leading-relaxed">{faq.answer}</p>
                          <Button
                            size="sm"
                            variant={added ? "secondary" : "outline"}
                            className="h-6 text-xs px-2 mt-1"
                            disabled={added}
                            onClick={() => addFaq(msgIdx, faqIdx, faq)}
                          >
                            {added ? (
                              <><CheckCheck className="w-3 h-3 mr-1" /> Added</>
                            ) : (
                              <><Plus className="w-3 h-3 mr-1" /> Add to SOP</>
                            )}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="flex items-center gap-1.5">
                <Bot className="w-3.5 h-3.5 text-primary" />
                <div className="bg-muted/60 rounded-xl px-3.5 py-2.5">
                  <div className="flex gap-1 items-center h-4">
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:0ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:150ms]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:300ms]" />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="px-4 py-3 border-t bg-background">
        <div className="flex gap-2 items-end">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your answer… (Enter to send)"
            className="min-h-[48px] max-h-32 resize-none text-sm"
            rows={1}
            disabled={loading}
            data-testid="input-sop-builder-chat"
          />
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="shrink-0 h-10 w-10 p-0"
            data-testid="button-sop-builder-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1.5">
          Shift+Enter for new line · AI looks up your real goals, schedule & habits
        </p>
      </div>
    </div>
  );
}
